import { supabase } from '../supabase/client';
import { getErrorMessage, getErrorName, isError } from '../errors';
import { trackEvent } from '../../app-core/analytics/posthog';

const MAX_RETRIES = 3;
const MAX_BACKOFF_MS = 5 * 60 * 1000;

export interface FetchOptions extends RequestInit {
  maxRetries?: number;
}

interface SessionData {
  access_token: string;
  expires_at?: number;
}

/**
 * Get current access token from Supabase session
 * Always fetches fresh session to avoid stale tokens
 */
export async function getCurrentAccessToken(): Promise<string | null> {
  try {
    console.log('🔐 Getting current session...');
    
    // Add timeout to prevent hanging on getSession()
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<{ data: { session: null }, error: Error }>((_, reject) => 
      setTimeout(() => reject(new Error('getSession timeout')), 5000)
    );
    
    const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]);
    
    if (error) {
      console.error('🔐 Failed to get session:', error);
      return null;
    }
    
    if (!session?.access_token) {
      console.warn('🔐 No access token in session');
      return null;
    }
    
    console.log('✅ Got access token from session');
    return session.access_token;
  } catch (error) {
    console.error('🔐 Exception getting access token:', error);
    return null;
  }
}

/**
 * Force refresh Supabase session if possible
 */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    console.log('🔄 Attempting to refresh access token...');
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('🔄 Failed to refresh session:', error);
      return null;
    }
    
    if (!session?.access_token) {
      console.warn('🔄 No access token after refresh');
      return null;
    }
    
    console.log('✅ Token refreshed successfully');
    return session.access_token;
  } catch (error) {
    console.error('🔄 Exception refreshing token:', error);
    return null;
  }
}

/**
 * Calculate exponential backoff delay
 */
function getBackoffDelay(retryCount: number): number {
  const delay = Math.min(1000 * 2 ** retryCount, MAX_BACKOFF_MS);
  return delay;
}

/**
 * Check if error indicates session loss
 */
function isSessionLost(response: Response): boolean {
  return response.status === 401 || response.status === 403;
}

/**
 * Authenticated fetch with automatic token refresh and retry logic
 * 
 * @param url - The URL to fetch
 * @param options - Request options (can include maxRetries)
 * @returns Promise<Response>
 * @throws - On permanent failure or session loss after retries
 */
export async function makeAuthenticatedFetch(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { maxRetries = MAX_RETRIES, ...fetchOptions } = options;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const accessToken = await getCurrentAccessToken();
      
      if (!accessToken) {
        console.error('🔐 No access token available');
        throw new Error('No access token - user may need to login');
      }
      
      console.log(`📡 Authenticated fetch: ${options.method || 'GET'} ${url}`, {
        attempt: attempt + 1,
        maxAttempts: maxRetries + 1
      });
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn(`⏱️ Request timeout after 10s: ${url}`);
        controller.abort();
      }, 10000); // 10 second timeout per request
      
      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...fetchOptions.headers,
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        console.log(`✅ Fetch succeeded: ${options.method || 'GET'} ${url}`);
        return response;
      }
      
      if (isSessionLost(response)) {
        console.warn(`⚠️ 401 Unauthorized on attempt ${attempt + 1}/${maxRetries + 1}`);
        
        if (attempt < maxRetries) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            console.error('🔐 Failed to refresh token - session may be lost');
            throw new Error('Session expired - please login again');
          }
          
          const delay = getBackoffDelay(attempt);
          console.log(`⏳ Retrying after ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        } else {
          console.error('🔐 Max retries exceeded - session lost');
          throw new Error('Session expired - please login again');
        }
      }
      
      if (attempt < maxRetries && response.status >= 500) {
        const delay = getBackoffDelay(attempt);
        console.warn(`⚠️ Server error ${response.status}, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      return response;
    } catch (error: unknown) {
      lastError = isError(error) ? error : new Error(getErrorMessage(error));
      
      const errorName = getErrorName(error);
      if (errorName === 'AbortError') {
        console.error('⏱️ Request timed out');
        lastError = new Error('Request timed out - please check your connection');
        if (attempt < maxRetries) {
          const delay = getBackoffDelay(attempt);
          console.warn(`⚠️ Timeout, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
      
      const errorMessage = getErrorMessage(error);
      if (errorMessage?.includes('Session expired') || errorMessage?.includes('No access token')) {
        console.error('🔐 Session lost:', errorMessage);
        throw lastError;
      }
      
      if (attempt < maxRetries) {
        const delay = getBackoffDelay(attempt);
        console.warn(`⚠️ Fetch failed, retrying in ${delay}ms:`, errorMessage);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  console.error('❌ All fetch attempts failed:', lastError);
  throw lastError || new Error('Fetch failed after retries');
}

/**
 * Proactive token refresh — supplements Supabase autoRefreshToken as fail-safe.
 * 
 * Checks if the current token has exceeded the given threshold percentage
 * of its lifetime. If so, triggers a refresh.
 * 
 * SECURITY: Telemetry events NEVER include access_token, refresh_token, or session.id.
 * 
 * @param thresholdPercent - Percentage of lifetime after which to refresh (default: 75)
 * @returns New access token string if refreshed, null if not needed or failed
 */
export async function refreshAccessTokenProactively(
  thresholdPercent: number = 75
): Promise<string | null> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      return null;
    }

    // Calculate token lifetime percentage elapsed
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at ?? 0;
    // Supabase tokens are typically 1 hour (3600s). Estimate issued time.
    const tokenLifetime = 3600; // 1 hour default
    const issuedAt = expiresAt - tokenLifetime;
    const elapsed = now - issuedAt;
    const lifetimePercent = (elapsed / tokenLifetime) * 100;

    if (lifetimePercent < thresholdPercent) {
      // Token still has plenty of lifetime — no refresh needed
      return null;
    }

    // Token is past threshold — refresh needed
    if (import.meta.env.DEV) {
      console.log(`🔄 Proactive refresh: ${lifetimePercent.toFixed(0)}% elapsed (threshold: ${thresholdPercent}%)`);
    }

    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

    if (refreshError || !refreshData.session) {
      // SECURITY: Only log error type, NOT error message (may contain token fragments)
      trackEvent('auth.token_refresh_failed', {
        errorType: refreshError?.name || 'UnknownError',
        refreshTrigger: 'proactive',
        lifetimePercentAtRefresh: Math.round(lifetimePercent),
        // ❌ DO NOT include: access_token, refresh_token, session.id, error.message
      });
      return null;
    }

    // Success — emit sanitized telemetry
    trackEvent('auth.token_refresh_success', {
      expiresAt: refreshData.session.expires_at,
      refreshTrigger: 'proactive',
      lifetimePercentAtRefresh: Math.round(lifetimePercent),
      userId: refreshData.session.user?.id,
      // ❌ DO NOT include: access_token, refresh_token, session.id
    });

    return refreshData.session.access_token;
  } catch (error) {
    // Track failure without exposing sensitive data
    trackEvent('auth.token_refresh_failed', {
      errorType: isError(error) ? error.name : 'UnknownError',
      refreshTrigger: 'proactive',
      // ❌ DO NOT include: error.message (may contain tokens), stack trace
    });
    return null;
  }
}
